import { useEffect, useState, useRef } from "react";
import { useAnalysis } from "../AnalysisContext";
import { useIsMobile } from "../useIsMobile";
import KalibrasyonView from "./KalibrasyonView";
import { api } from "../api";

const TABS = ["Haftalık Sonuçlar","Puan Durumu","Takım İstatistikleri","Analizler"];

function normalizeName(s) {
  if (!s) return "";
  return s.toLowerCase()
    .replace(/ş/g,"s").replace(/ğ/g,"g").replace(/ü/g,"u")
    .replace(/ö/g,"o").replace(/ı/g,"i").replace(/ç/g,"c")
    .replace(/Ş/g,"s").replace(/Ğ/g,"g").replace(/Ü/g,"u")
    .replace(/Ö/g,"o").replace(/İ/g,"i").replace(/Ç/g,"c");
}

// Herkese açık referans (Süper Lig) + kullanıcının açtığı maçları birleştir.
// Public giriş gerektirmez; giriş varsa kullanıcı kaydı aynı maçta üstüne biner.
function fetchMergedPreds(league) {
  return Promise.all([
    api.getPublicPredictions(league).then(d => d.predictions || []).catch(() => []),
    api.getPredictions(league).then(d => d.predictions || []).catch(() => []),
  ]).then(([pub, mine]) => {
    const byKey = {};
    for (const r of pub)  byKey[`${r.home}|${r.away}|${r.match_date}`] = r;
    for (const r of mine) byKey[`${r.home}|${r.away}|${r.match_date}`] = r;
    return Object.values(byKey);
  });
}

function findPred(predictions, home, away) {
  if (!predictions) return null;
  return predictions.find(p =>
    normalizeName(p.home) === normalizeName(home) &&
    normalizeName(p.away) === normalizeName(away)
  ) || null;
}

function FormDots({ form }) {
  return (
    <div className="form-dots">
      {(form||"").split("").slice(0,5).map((c,i) => (
        <div key={i} className={`fd ${c==="W"?"fd-w":c==="L"?"fd-l":"fd-d"}`} />
      ))}
    </div>
  );
}

function TeamMatches({ league, team }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!team) return;
    setLoading(true);
    api.matches(league, 500).then(d => {
      const today = new Date().toISOString().slice(0,10);
      setMatches((d.matches||[])
        .filter(m=>m.home===team||m.away===team)
        .sort((a,b)=>{
          const da=Math.abs(new Date(a.date)-new Date(today));
          const db=Math.abs(new Date(b.date)-new Date(today));
          return da-db;
        })
        .slice(0,15));
      setLoading(false);
    });
  }, [league, team]);
  if (!team) return <div className="empty" style={{padding:20}}>← Puan durumundan takım seçin</div>;
  if (loading) return <div className="loading">Yükleniyor...</div>;
  return (
    <div className="card">
      <div className="card-header"><span className="card-title">{team} — Bu Sezon</span></div>
      {matches.length===0 && <div className="empty">Maç bulunamadı.</div>}
      {matches.map((m,i) => {
        const isHome=m.home===team, hw=m.home_goals>m.away_goals, aw=m.away_goals>m.home_goals;
        const win=(isHome&&hw)||(!isHome&&aw), lose=(isHome&&aw)||(!isHome&&hw);
        const rc=win?"#639922":lose?"#E24B4A":"#888780";
        return (
          <div key={i} className="match-row">
            <div className="match-date">{m.date?.slice(5)}</div>
            <div style={{fontSize:10,width:14,height:14,borderRadius:"50%",background:rc,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,flexShrink:0}}>{win?"G":lose?"M":"B"}</div>
            <div className="match-teams" style={{marginLeft:6}}>
              <div className={`match-team right ${isHome?"bold":""}`}>{m.home}</div>
              <div className="match-score">{m.home_goals} - {m.away_goals}</div>
              <div className={`match-team ${!isHome?"bold":""}`}>{m.away}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Eleme turları — API etiketi → Türkçe ad. Sıra bracket sırasıdır, açılır
// listede de bu sırayla görünür.
const KNOCKOUT_ROUNDS = [
  ["Round of 32",     "Son 32"],
  ["Round of 16",     "Son 16"],
  ["Quarter-finals",  "Çeyrek Final"],
  ["Semi-finals",     "Yarı Final"],
  ["3rd Place Final", "3.'lük Maçı"],
  ["Final",           "Final"],
];

function Standings({ league, allMatches = [] }) {
  const matches = allMatches;
  const [data,setData]=useState([]);
  const [loading,setLoading]=useState(true);
  const [sel,setSel]=useState("");
  const [view,setView]=useState("");   // "g:<grup>" | "r:<tur>"
  useEffect(()=>{
    setLoading(true); setSel(""); setView("");
    api.standings(league).then(d=>{
      const rows = d.standings||[];
      setData(rows);
      if (rows.length > 0) setSel(rows[0].team);
      setLoading(false); // sel ile aynı batch'te değil — ayrı bir effect ile çöz
    });
  },[league]);

  // Turnuvalarda (Dünya Kupası, Şampiyonlar Ligi, elemeler) backend birden çok
  // grup döndürür; liglerde grup alanı tek değerlidir ve seçici gösterilmez.
  const groups = [...new Set(data.map(r=>r.group).filter(Boolean))];
  const koRounds = KNOCKOUT_ROUNDS.filter(([api_])=>matches.some(m=>m.round===api_));

  const options = [
    ...(groups.length > 1 ? groups.map(g=>["g:"+g, g]) : []),
    ...koRounds.map(([api_,tr])=>["r:"+api_, tr]),
  ];
  const hasSelector = options.length > 1;
  const activeView  = (hasSelector && options.some(([v])=>v===view)) ? view : (options[0]?.[0] || "");
  const isRound     = activeView.startsWith("r:");
  const activeRound = isRound ? activeView.slice(2) : "";
  const activeGroup = !isRound && activeView ? activeView.slice(2) : "";

  const rows = activeGroup ? data.filter(r=>r.group===activeGroup) : (groups.length > 1 ? [] : data);
  const roundMatches = isRound
    ? matches.filter(m=>m.round===activeRound)
             .sort((a,b)=>(a.date||"").localeCompare(b.date||""))
    : [];

  // Grup değişince seçili takımı o gruba taşı — aksi halde sağdaki maç listesi
  // görünmeyen bir takımı gösterir.
  useEffect(()=>{
    if (rows.length && !rows.some(r=>r.team===sel)) setSel(rows[0].team);
  },[activeGroup, data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="loading">Yükleniyor...</div>;
  if (!data.length) return <div className="empty">Puan durumu bulunamadı.</div>;
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))",gap:12}}>
      <div className="card">
        <div className="card-header">
          <span className="card-title">{isRound ? "Eleme Turu" : "Puan Durumu"}</span>
          {hasSelector ? (
            <select value={activeView} onChange={e=>setView(e.target.value)}
              style={{fontSize:11,padding:"3px 6px",borderRadius:5,
                      border:"1px solid #e5e7eb",background:"#fff",cursor:"pointer"}}>
              {groups.length > 1 && (
                <optgroup label="Gruplar">
                  {groups.map(g=><option key={g} value={"g:"+g}>{g}</option>)}
                </optgroup>
              )}
              {koRounds.length > 0 && (
                <optgroup label="Eleme Turları">
                  {koRounds.map(([api_,tr])=><option key={api_} value={"r:"+api_}>{tr}</option>)}
                </optgroup>
              )}
            </select>
          ) : (
            <span style={{fontSize:10,color:"#aaa"}}>Takıma tıkla →</span>
          )}
        </div>
        {isRound && (
          roundMatches.length ? (
            <div style={{padding:"4px 0"}}>
              {roundMatches.map((m,i)=>{
                const played = m.home_goals!=null && m.away_goals!=null;
                const ek = m.status==="AET" ? "120'" : m.status==="PEN" ? "Pen." : "";
                return (
                  <div key={`${m.date}-${m.home}-${m.away}-${i}`}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",
                            borderBottom:"1px solid #f1f5f9",fontSize:13}}>
                    <span style={{width:52,color:"#94a3b8",fontSize:11}}>{formatDate(m.date)}</span>
                    {ek && <span style={{fontSize:10,color:"#dc2626",fontWeight:700,width:30}}>{ek}</span>}
                    <span style={{flex:1,textAlign:"right",
                                  fontWeight:played && m.home_goals>m.away_goals?700:400}}>{m.home}</span>
                    <span style={{minWidth:46,textAlign:"center",fontWeight:700,
                                  background:"#f8fafc",borderRadius:5,padding:"2px 6px"}}>
                      {played ? `${m.home_goals} - ${m.away_goals}` : "-"}
                    </span>
                    <span style={{flex:1,
                                  fontWeight:played && m.away_goals>m.home_goals?700:400}}>{m.away}</span>
                  </div>
                );
              })}
            </div>
          ) : <div className="empty">Bu turda maç bulunamadı.</div>
        )}
        {!isRound && (
        <table className="standings-table">
          <thead><tr><th>#</th><th>Takım</th><th className="num">O</th><th className="num hide-mobile">G</th><th className="num hide-mobile">B</th><th className="num hide-mobile">M</th><th className="num">AG</th><th className="num">P</th><th className="hide-mobile">Form</th></tr></thead>
          <tbody>
            {rows.map(row=>{
              const rc=row.rank===1?"rank-1":row.rank===2?"rank-2":row.rank===3?"rank-3":"rank-n";
              const isSel=sel===row.team;
              return (
                <tr key={row.team} className={row.rank<=3?"highlight":""} onClick={()=>setSel(row.team)} style={{cursor:"pointer",background:isSel?"#f0f9ff":""}}>
                  <td><span className={`rank-badge ${rc}`}>{row.rank}</span></td>
                  <td><strong style={{color:isSel?"#2563eb":""}}>{row.team}</strong></td>
                  <td className="num">{row.played??"-"}</td><td className="num hide-mobile">{row.wins??"-"}</td>
                  <td className="num hide-mobile">{row.draws??"-"}</td><td className="num hide-mobile">{row.losses??"-"}</td>
                  <td className="num">{row.gd>0?`+${row.gd}`:row.gd}</td>
                  <td className="num"><strong>{row.points}</strong></td>
                  <td className="hide-mobile"><FormDots form={row.form}/></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
      <TeamMatches league={league} team={sel}/>
    </div>
  );
}

function getWeekRange(date) {
  const d=new Date(date), day=d.getDay(), diff=(day-2+7)%7;
  const tue=new Date(d); tue.setDate(d.getDate()-diff);
  const mon=new Date(tue); mon.setDate(tue.getDate()+6);
  return {start:tue.toISOString().slice(0,10),end:mon.toISOString().slice(0,10)};
}
function getCurrentWeekRange(){return getWeekRange(new Date());}
function formatDate(s){
  if(!s)return"";
  const months=["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  const d=new Date(s); return `${d.getDate()} ${months[d.getMonth()]}`;
}

function StatusIcon({ pred }) {
  if (!pred) return (
    <div title="Analiz yapılmamış" style={{
      width:20,height:20,borderRadius:"50%",
      border:"2px solid #d1d5db",background:"#f9fafb",flexShrink:0,
    }}/>
  );
  if (pred.is_correct === true) return (
    <div title="Doğru tahmin" style={{
      width:20,height:20,borderRadius:"50%",
      background:"#22c55e",
      display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:12,color:"#fff",fontWeight:700,flexShrink:0,
    }}>✓</div>
  );
  if (pred.is_correct === false) return (
    <div title="Yanlış tahmin" style={{
      width:20,height:20,borderRadius:"50%",
      background:"#ef4444",
      display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:12,color:"#fff",fontWeight:700,flexShrink:0,
    }}>✗</div>
  );
  return (
    <div title="Analiz yapıldı, maç bekleniyor" style={{
      width:20,height:20,borderRadius:"50%",
      background:"#2563eb",
      display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:12,color:"#fff",fontWeight:700,flexShrink:0,
    }}>✓</div>
  );
}

function WeeklyResults({ league, allMatches, loading, onPredict, onBulkPredict, onViewPrediction, predictions, queue, userAnalysisEnabled }) {
  const isMobile = useIsMobile();
  const [selPast,setSelPast]=useState("");
  const [checked,setChecked]=useState({});

  // Lig değişince checked sıfırla
  useEffect(()=>{ setChecked({}); setSelPast(""); },[league]);

  if (loading) return <div className="loading">Yükleniyor...</div>;

  const today=new Date().toISOString().slice(0,10);
  const cw=getCurrentWeekRange();
  const thisWeek=allMatches.filter(m=>m.date>=cw.start&&m.date<=cw.end).sort((a,b)=>a.date.localeCompare(b.date));

  const isLiveStatus = (s) => s && s !== "FT" && s !== "NS" && s !== "";
  const isPlayed = (m) => (m.home_goals!=null && m.date<=today) || (isLiveStatus(m.status) && m.home_goals!=null);

  const pastWeeks={};
  allMatches.filter(m=>m.date<cw.start).forEach(m=>{
    const {start,end}=getWeekRange(m.date);
    if(!pastWeeks[start])pastWeeks[start]={range:{start,end},matches:[]};
    pastWeeks[start].matches.push(m);
  });
  const pastKeys=Object.keys(pastWeeks).sort().reverse();
  const activeKey=selPast||pastKeys[0]||"";
  const pastMatches=activeKey?(pastWeeks[activeKey]?.matches||[]).sort((a,b)=>a.date.localeCompare(b.date)):[];

  const matchKey = m => `${m.home}|${m.away}|${m.date}`;
  const upcoming = thisWeek.filter(m => !isPlayed(m));
  const selectedMatches = upcoming.filter(m => checked[matchKey(m)]);
  const allChecked = upcoming.length > 0 && upcoming.every(m => checked[matchKey(m)]);

  const toggleCheck = (m) => {
    const k = matchKey(m);
    setChecked(prev => ({...prev, [k]: !prev[k]}));
  };
  const toggleAll = () => {
    if (allChecked) {
      const next = {...checked};
      upcoming.forEach(m => { next[matchKey(m)] = false; });
      setChecked(next);
    } else {
      const next = {...checked};
      upcoming.forEach(m => { next[matchKey(m)] = true; });
      setChecked(next);
    }
  };


  const renderPlayed = (m, i) => {
    const hw = m.home_goals>m.away_goals;
    const aw = m.away_goals>m.home_goals;
    const pred = findPred(predictions, m.home, m.away);
    const live = isLiveStatus(m.status);
    return (
      <div key={i} style={{
        display:"grid", gridTemplateColumns:"20px 44px 1fr 60px 1fr",
        alignItems:"center", padding:"8px 14px",
        borderBottom:"1px solid #f0f0f0",
        background: live ? "#fff9f0" : "#fff", gap:6,
      }}>
        <StatusIcon pred={pred}/>
        <div style={{fontSize:12,color: live ? "#f59e0b" : "#aaa",whiteSpace:"nowrap",fontWeight: live ? 600 : 400}}>
          {live ? `🔴 ${m.elapsed != null ? m.elapsed + "'" : m.status}` : formatDate(m.date)}
        </div>
        <div style={{textAlign:"right",fontSize:13,fontWeight:hw?700:500,color:"#222",paddingRight:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.home}</div>
        <div style={{textAlign:"center"}}>
          <span style={{display:"inline-block",fontSize:13,fontWeight:700,color: live ? "#f59e0b" : "#333",background: live ? "#fff3cd" : "#f0f0f0",padding:"2px 8px",borderRadius:4,minWidth:52,textAlign:"center"}}>
            {m.home_goals} - {m.away_goals}
          </span>
        </div>
        <div style={{fontSize:13,fontWeight:aw?700:500,color:"#222",paddingLeft:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.away}</div>
      </div>
    );
  };

  const renderUpcoming = (m, i) => {
    const k = matchKey(m);
    const isChecked = !!checked[k];
    const pred = findPred(predictions, m.home, m.away);
    return (
      <div key={i} style={{
        display:"grid", gridTemplateColumns:"20px 16px 44px 1fr 60px 1fr",
        alignItems:"center", padding:"8px 14px",
        borderBottom:"1px solid #f0f0f0",
        background: (() => { const qItem=(queue||[]).find(q=>q.home===m.home&&q.away===m.away); const a=qItem?.status==="loading"||qItem?.status==="polling"; return a ? "#fffbeb" : qItem?.status==="waiting" ? "#fefce8" : isChecked ? "#eff6ff" : "#eef6ff"; })(),
        gap:6,
      }}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => toggleCheck(m)}
          style={{width:14,height:14,cursor:"pointer",accentColor:"#2563eb"}}
        />
        {(() => {
          const qItem = (queue||[]).find(q=>q.home===m.home&&q.away===m.away);
          // "polling" = job başladı, pipeline (~60sn) sürüyor — asıl analiz bu
          // aşamada. Eskiden yalnızca "loading" (kısa POST anı) spinner
          // gösteriyordu, o yüzden analizin çoğu boyunca kum saati görünmüyordu.
          if (qItem?.status==="loading" || qItem?.status==="polling") return (
            <span style={{width:16,height:16,borderRadius:"50%",background:"#fef3c7",border:"2px solid #fcd34d",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,flexShrink:0,animation:"spin 1s linear infinite"}}>⏳</span>
          );
          if (qItem?.status==="waiting") return (
            <span style={{width:16,height:16,borderRadius:"50%",background:"#fef9c3",border:"2px solid #fef08a",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,flexShrink:0}}>⏸</span>
          );
          if (pred) return (
            <div title="Analiz edildi" style={{minWidth:18,height:16,borderRadius:3,background:"#16a34a",border:"none",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff",fontWeight:700,flexShrink:0,padding:"0 3px"}}>{(() => {
              const pt=(pred?.gpt_prediction||pred?.claude_prediction||pred?.prediction||"").toLowerCase();
              if(pt.includes("beraberlik")) return "X";
              if(pt.includes("deplasman")||pt.includes("away")) return "2";
              return "1";
            })()}</div>
          );
          return (
            <div style={{width:16,height:16,borderRadius:"50%",background:"transparent",border:"2px solid #d1d5db",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#9ca3af",fontWeight:700,flexShrink:0}}/>
          );
        })()}
        <div style={{fontSize:12,color:"#aaa",whiteSpace:"nowrap"}}>{formatDate(m.date)}</div>
        <div style={{textAlign:"right",fontSize:13,fontWeight:500,color:"#222",paddingRight:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.home}</div>
        <div style={{textAlign:"center"}}>
          <span style={{fontSize:12,color:"#93c5fd",fontWeight:600}}>vs</span>
        </div>
        <div style={{fontSize:13,fontWeight:500,color:"#222",paddingLeft:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.away}</div>
        {(() => {
          const qItem=(queue||[]).find(q=>q.home===m.home&&q.away===m.away);
          if (qItem?.status==="loading" || qItem?.status==="polling") return <span style={{fontSize:10,color:"#d97706",fontWeight:600,whiteSpace:"nowrap",gridColumn:"span 1"}}>⏳ Analiz ediliyor...</span>;
          if (qItem?.status==="waiting") return <span style={{fontSize:10,color:"#a16207",whiteSpace:"nowrap",gridColumn:"span 1"}}>⏸ Bekliyor</span>;
          return null;
        })()}
      </div>
    );
  };

  const ActionBar = () => {
    if (upcoming.length === 0) return null;
    const n = selectedMatches.length;
    const canAnalyze = userAnalysisEnabled !== false;
    return (
      <div style={{
        padding:"10px 14px",
        borderTop:"1px solid #e5e5e5",
        background:"#f9f9f9",
        display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",
      }}>
        <label style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"#555",cursor:"pointer",userSelect:"none"}}>
          <input type="checkbox" checked={allChecked} onChange={toggleAll} style={{accentColor:"#2563eb"}}/>
          Tümü
        </label>
        <span style={{fontSize:11,color:"#aaa"}}>{n>0?`${n} maç seçili`:""}</span>
        <div style={{marginLeft:"auto"}}>
          <button
            disabled={!canAnalyze || n===0}
            onClick={() => {
              if (!canAnalyze || n===0) return;
              if (n===1) onPredict && onPredict(selectedMatches[0], league);
              else onBulkPredict && onBulkPredict(selectedMatches, league);
            }}
            style={{
              fontSize:12, padding:"5px 14px", borderRadius:5,
              border: !canAnalyze ? "1px solid #e5e7eb" : n===0 ? "1px solid #d1d5db" : "1px solid #2563eb",
              background:"#fff",
              color: !canAnalyze ? "#d1d5db" : n===0 ? "#9ca3af" : "#2563eb",
              cursor: !canAnalyze || n===0 ? "default" : "pointer",
              fontWeight:500,
              opacity: !canAnalyze ? 0.5 : 1,
            }}
          >
            🎯 {n>1?"Toplu Analiz":"Analiz Et"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
      <div className="card">
        <div className="card-header">
          <span className="card-title">Bu Hafta</span>
          <span style={{fontSize:11,color:"#aaa"}}>{formatDate(cw.start)} - {formatDate(cw.end)}</span>
        </div>
        {thisWeek.length===0
          ? <div className="empty">Bu hafta maç yok.</div>
          : thisWeek.map((m,i) => {
              const played = isPlayed(m);
              return played ? renderPlayed(m,i) : renderUpcoming(m,i);
            })
        }
        <ActionBar/>
      </div>
      <div className="card">
        <div className="card-header">
          <span className="card-title">Geçmiş Haftalar</span>
          <select value={activeKey} onChange={e=>setSelPast(e.target.value)} style={{fontSize:11,padding:"2px 6px",border:"0.5px solid #e5e5e5",borderRadius:4,background:"#fff"}}>
            {pastKeys.map(k=><option key={k} value={k}>{formatDate(pastWeeks[k].range.start)} - {formatDate(pastWeeks[k].range.end)}</option>)}
          </select>
        </div>
        {pastMatches.length===0
          ? <div className="empty">Maç bulunamadı.</div>
          : pastMatches.map(renderPlayed)
        }
      </div>
    </div>
  );
}

function StatBox({title,emoji,rows,valSuffix=""}) {
  const max=Math.max(...rows.map(r=>r.val),1);
  return (
    <div className="card">
      <div className="card-header"><span className="card-title">{emoji} {title}</span></div>
      {rows.slice(0,5).map((r,i)=>(
        <div key={r.team} className="team-stat-row">
          <div className="ts-rank">{i+1}</div>
          <div className="ts-name">{r.team}</div>
          <div className="ts-bar-bg"><div className="ts-bar" style={{width:`${(r.val/max)*100}%`}}/></div>
          <div className="ts-val">{r.val}{valSuffix}</div>
        </div>
      ))}
    </div>
  );
}

function TeamStats({league, allMatches}) {
  const [data,setData]=useState([]);
  const [loading,setLoading]=useState(true);
  const [seasons,setSeasons]=useState([]);
  const [current,setCurrent]=useState("");
  const [season,setSeason]=useState("");      // kullanici secimi ("" = otomatik)
  const [resolved,setResolved]=useState("");  // backend'in fiilen kullandigi sezon
  const [pastMatches,setPastMatches]=useState([]);

  // Bu sayfa iki kaynaktan besleniyor: kart verilerinin bir kismi api.stats'tan
  // (sezon bazli, CSV), bir kismi mac listesinden hesaplaniyor. Ikisi ayni
  // sezona bakmazsa ekranda iki farkli sezon karisir — 20 Tem 2026'ya kadar
  // tam olarak bu oluyordu. O yuzden secilen sezon ikisine birden uygulanir.
  const isCurrent = !resolved || resolved === current;
  const matches = isCurrent ? (allMatches || []) : pastMatches;

  useEffect(()=>{
    api.seasons(league)
      .then(d=>{ setSeasons(d.seasons||[]); setCurrent(d.current||""); })
      .catch(()=>{ setSeasons([]); setCurrent(""); });
    setSeason(""); setResolved("");
  },[league]);

  useEffect(()=>{
    setLoading(true);
    api.stats(league, season||undefined).then(s=>{
      setData(s.stats||[]);
      // Backend'in sectigi sezon AYRI state'te tutulur. Ayni state'e yazsaydik
      // effect kendi bagimliligini degistirip bir tur daha donerdi.
      setResolved(s.season||"");
      setLoading(false);
    }).catch(()=>setLoading(false));
  },[league, season]);

  // Guncel sezon disinda maclar da o sezondan cekilir (arsiv CSV'den).
  useEffect(()=>{
    if (isCurrent || !resolved) { setPastMatches([]); return; }
    api.matches(league, 500, resolved)
      .then(d=>setPastMatches(d.matches||[]))
      .catch(()=>setPastMatches([]));
  },[league, resolved, isCurrent]);

  const seasonPicker = seasons.length > 1 ? (
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
      <span style={{fontSize:12,color:"#64748b"}}>Sezon</span>
      <select value={season || resolved} onChange={e=>setSeason(e.target.value)}
        style={{fontSize:12,padding:"4px 8px",borderRadius:6,
                border:"1px solid #e5e7eb",background:"#fff",cursor:"pointer"}}>
        {seasons.map(s=>(
          <option key={s} value={s}>{s}{s===current?" (güncel)":""}</option>
        ))}
      </select>
      {resolved && resolved!==current && (
        <span style={{fontSize:11,color:"#b45309",background:"#fffbeb",
                      border:"1px solid #fde68a",borderRadius:5,padding:"2px 8px"}}>
          Geçmiş sezon — güncel sezon {current} henüz başlamadı
        </span>
      )}
    </div>
  ) : null;

  if(loading)return<div className="loading">Yükleniyor...</div>;
  if(!data.length)return<div>{seasonPicker}<div className="empty">Veri bulunamadı.</div></div>;

  const byGF=[...data].sort((a,b)=>b.goals_for-a.goals_for);
  const byGA=[...data].sort((a,b)=>a.goals_against-b.goals_against);
  const byAvg=[...data].sort((a,b)=>b.avg_goals_for-a.avg_goals_for);
  const ts={};data.forEach(t=>{ts[t.team]={homeW:0,homeP:0,awayW:0,awayP:0};});
  matches.forEach(m=>{
    if(!m.home||!m.away||m.home_goals==null)return;
    if(ts[m.home]){ts[m.home].homeP++;if(m.home_goals>m.away_goals)ts[m.home].homeW++;}
    if(ts[m.away]){ts[m.away].awayP++;if(m.away_goals>m.home_goals)ts[m.away].awayW++;}
  });
  const hwp=Object.entries(ts).filter(([,v])=>v.homeP>=3).map(([t,v])=>({team:t,val:Math.round(v.homeW/v.homeP*100)})).sort((a,b)=>b.val-a.val);
  const awp=Object.entries(ts).filter(([,v])=>v.awayP>=3).map(([t,v])=>({team:t,val:Math.round(v.awayW/v.awayP*100)})).sort((a,b)=>b.val-a.val);
  const bm={};data.forEach(t=>{bm[t.team]={total:0,btts:0};});
  matches.forEach(m=>{
    if(!m.home||!m.away||m.home_goals==null)return;
    const b=m.home_goals>0&&m.away_goals>0;
    if(bm[m.home]){bm[m.home].total++;if(b)bm[m.home].btts++;}
    if(bm[m.away]){bm[m.away].total++;if(b)bm[m.away].btts++;}
  });
  const btts=Object.entries(bm).filter(([,v])=>v.total>=5).map(([t,v])=>({team:t,val:Math.round(v.btts/v.total*100)})).sort((a,b)=>b.val-a.val);
  const sm={};data.forEach(t=>{sm[t.team]=0;});
  Object.keys(sm).forEach(team=>{
    let s=0;
    for(const m of[...matches].sort((a,b)=>b.date?.localeCompare(a.date))){
      if(!m.home||!m.away||m.home_goals==null)continue;
      if(m.home!==team&&m.away!==team)continue;
      const won=(m.home===team&&m.home_goals>m.away_goals)||(m.away===team&&m.away_goals>m.home_goals);
      if(won)s++;else break;
    }
    sm[team]=s;
  });
  const ws=Object.entries(sm).map(([t,v])=>({team:t,val:v})).sort((a,b)=>b.val-a.val).filter(r=>r.val>0);
  const gr=[...data].filter(t=>t.goals_against>0).map(t=>({team:t.team,val:Math.round(t.goals_for/t.goals_against*100)/100})).sort((a,b)=>b.val-a.val);

  return (
    <div>
      {seasonPicker}
      <div className="three-col" style={{marginBottom:12}}>
        <StatBox title="En Çok Gol Atan" emoji="⚽" rows={byGF.map(t=>({team:t.team,val:t.goals_for}))}/>
        <StatBox title="En İyi Savunma" emoji="🛡️" rows={byGA.map(t=>({team:t.team,val:t.goals_against}))}/>
        <StatBox title="Ort. Gol/Maç" emoji="📊" rows={byAvg.map(t=>({team:t.team,val:t.avg_goals_for}))}/>
      </div>
      <div className="three-col" style={{marginBottom:12}}>
        <StatBox title="Ev Galibiyet %" emoji="🏠" rows={hwp} valSuffix="%"/>
        <StatBox title="Deplasman Galibiyet %" emoji="✈️" rows={awp} valSuffix="%"/>
        <StatBox title="Gol/Yenilen Oranı" emoji="⚖️" rows={gr}/>
      </div>
      <div className="three-col" style={{marginBottom:12}}>
        <StatBox title="KG Var % (BTTS)" emoji="🎯" rows={btts} valSuffix="%"/>
        {(()=>{
          const hm={};data.forEach(t=>{hm[t.team]={total:0,goals:0};});
          matches.forEach(m=>{
            if(!m.home||!m.away)return;
            const hthg=m.hthg??m.HTHG,htag=m.htag??m.HTAG;
            if(hthg==null||htag==null)return;
            if(hm[m.home]){hm[m.home].total++;hm[m.home].goals+=Number(hthg);}
            if(hm[m.away]){hm[m.away].total++;hm[m.away].goals+=Number(htag);}
          });
          const ha=Object.entries(hm).filter(([,v])=>v.total>=5).map(([t,v])=>({team:t,val:Math.round(v.goals/v.total*100)/100})).sort((a,b)=>b.val-a.val);
          return<StatBox title="İlk Yarı Ort. Gol" emoji="⏱️" rows={ha}/>;
        })()}
        <StatBox title="Galibiyet Serisi" emoji="🏆" rows={ws}/>
      </div>
      <div className="three-col" style={{marginBottom:12}}>
        <StatBox title="En Çok Beraberlik" emoji="🤝" rows={[...data].sort((a,b)=>b.draws-a.draws).map(t=>({team:t.team,val:t.draws}))}/>
        <StatBox title="2.5 Altı Maç" emoji="🔒" rows={Object.entries((()=>{const m={};data.forEach(t=>{m[t.team]=0;});matches.forEach(x=>{if(!x.home||!x.away||x.home_goals==null)return;if(x.home_goals+x.away_goals<=2){if(m[x.home]!==undefined)m[x.home]++;if(m[x.away]!==undefined)m[x.away]++;}});return m;})()).map(([t,v])=>({team:t,val:v})).sort((a,b)=>b.val-a.val)}/>
        <StatBox title="2.5 Üstü %" emoji="🔥" rows={Object.entries((()=>{const o={};data.forEach(t=>{o[t.team]={total:0,over:0};});matches.forEach(m=>{if(!m.home||!m.away||m.home_goals==null)return;const tot=m.home_goals+m.away_goals;if(o[m.home]){o[m.home].total++;if(tot>2)o[m.home].over++;}if(o[m.away]){o[m.away].total++;if(tot>2)o[m.away].over++;}});return o;})()).filter(([,v])=>v.total>=5).map(([t,v])=>({team:t,val:Math.round(v.over/v.total*100)})).sort((a,b)=>b.val-a.val)} valSuffix="%"/>
      </div>
    </div>
  );
}

// ─── Lig Tahmin Paneli ────────────────────────────────────────────────────────
const C_AI = {
  gpt: { color: "#10b981", bg: "#ecfdf5", icon: "🟢" },
};

function normPred(pred) {
  if (!pred) return null;
  const p = pred.toLowerCase();
  if (p.includes("beraberlik")) return "draw";
  if (p.includes("deplasman") || p === "away_win") return "away";
  return "home";
}

function PredLabel({ pred, home, away }) {
  if (!pred) return <span style={{ color: "#d1d5db" }}>—</span>;
  const n = normPred(pred);
  if (n === "draw") return <span>🤝 Beraberlik</span>;
  if (n === "away")  return <span>✈️ {away}</span>;
  return <span>🏠 {home}</span>;
}

function ScorePill({ hg, ag }) {
  if (hg == null || ag == null) return <span style={{ color: "#d1d5db" }}>—</span>;
  return <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{hg} – {ag}</span>;
}

function ResultDot({ pred, actual }) {
  if (!pred || !actual) return null;
  const correct = normPred(pred) === (
    actual === "draw" ? "draw" : actual === "away_win" ? "away" : "home"
  );
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 18, height: 18, borderRadius: "50%", fontSize: 10, fontWeight: 700,
      background: correct ? "#f0fdf4" : "#fef2f2",
      color: correct ? "#16a34a" : "#dc2626",
      border: `1px solid ${correct ? "#bbf7d0" : "#fecaca"}`,
    }}>{correct ? "✓" : "✗"}</span>
  );
}

function LeaguePredictions({ league, predictions, onPredict, onBulkPredict }) {
  const [reportPred, setReportPred] = useState(null);
  const [activeReport, setActiveReport] = useState("form");

  // Sadece bu ligin tahminleri, tarihe göre sırala
  const ligPreds = predictions
    .filter(p => p.league === league)
    .sort((a, b) => (b.match_date || "").localeCompare(a.match_date || ""));

  if (ligPreds.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
        <div style={{ fontSize: 14, marginBottom: 6 }}>Bu lig için henüz analiz yapılmamış.</div>
        <div style={{ fontSize: 12 }}>Haftalık Sonuçlar sekmesinden maç seçip analiz başlatabilirsin.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Özet */}
      {(() => {
        const done = ligPreds.filter(p => p.actual_result);
        const gs = { correct: 0, total: 0 };
        done.forEach(p => {
          const pr = p.gpt_prediction || (p.ai_provider === "gpt" ? p.prediction : null);
          if (!pr) return;
          gs.total++;
          const norm = normPred(pr);
          const act = p.actual_result === "draw" ? "draw" : p.actual_result === "away_win" ? "away" : "home";
          if (norm === act) gs.correct++;
        });
        const acc = gs.total > 0 ? Math.round(gs.correct / gs.total * 100) : null;
        return (
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 16px" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.8 }}>Toplam</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#111" }}>{ligPreds.length}</div>
            </div>
            {gs.total > 0 && (
              <div style={{ background: C_AI.gpt.bg, border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16 }}>{C_AI.gpt.icon}</span>
                <div>
                  <div style={{ fontSize: 11, color: C_AI.gpt.color, fontWeight: 600 }}>GPT-4o</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {gs.correct}/{gs.total}
                    {acc !== null && <span style={{ marginLeft: 6, fontWeight: 700, color: acc >= 50 ? "#16a34a" : "#dc2626" }}>%{acc}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Tablo */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                {["Maç", "Tarih", "🟢 GPT-4o", "İY", "MS", "Sonuç", ""].map((h, i) => (
                  <th key={i} style={{ padding: "9px 12px", textAlign: "left", fontSize: 11,
                    color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5,
                    fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ligPreds.map((p, i) => {
                const gptPred    = p.gpt_prediction    || (p.ai_provider === "gpt"    ? p.prediction : null);
                const actual     = p.actual_result;
                const actualLabel = actual === "draw" ? "🤝 Beraberlik"
                  : actual === "away_win" ? `✈️ ${p.away}` : actual === "home_win" ? `🏠 ${p.home}` : null;

                return (
                  <tr key={p.id} onClick={() => { setReportPred(p); setActiveReport("form"); }}
                    style={{ borderBottom: "1px solid #f3f4f6",
                    background: i % 2 === 0 ? "#fff" : "#fafafa",
                    cursor: "pointer" }}
                    title="Raporu görüntüle">

                    {/* Maç */}
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      <span style={{ fontWeight: 600, color: "#111" }}>{p.home}</span>
                      <span style={{ color: "#d1d5db", margin: "0 5px", fontSize: 11 }}>vs</span>
                      <span style={{ fontWeight: 600, color: "#111" }}>{p.away}</span>
                    </td>

                    {/* Tarih */}
                    <td style={{ padding: "9px 12px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                      {p.match_date || "—"}
                    </td>

                    {/* GPT */}
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {gptPred ? (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: C_AI.gpt.color }}>
                            <PredLabel pred={gptPred} home={p.home} away={p.away} />
                          </div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>
                            {p.gpt_home_pct}% / {p.gpt_draw_pct}% / {p.gpt_away_pct}%
                          </div>
                        </div>
                      ) : <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>}
                    </td>

                    {/* İY */}
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                      <ScorePill hg={p.ht_home_goals} ag={p.ht_away_goals} />
                    </td>

                    {/* MS */}
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                      <ScorePill hg={p.ft_home_goals} ag={p.ft_away_goals} />
                    </td>

                    {/* Sonuç */}
                    <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                      {actualLabel ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 12, color: "#374151" }}>{actualLabel}</span>
                          <ResultDot pred={gptPred} actual={actual} />
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: "#d1d5db" }}>Bekleniyor</span>
                      )}
                    </td>

                    {/* Rapor ikonu */}
                    <td style={{ padding: "9px 12px", color: "#9ca3af", fontSize: 13 }}>👁</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rapor Modalı */}
      {reportPred && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20 }} onClick={() => setReportPred(null)}>
          <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 680,
            maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{reportPred.home} vs {reportPred.away}</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>{reportPred.league} · {reportPred.match_date}</div>
              </div>
              <button onClick={() => setReportPred(null)} style={{ fontSize: 18, border: "none",
                background: "none", cursor: "pointer", color: "#9ca3af" }}>✕</button>
            </div>

            {/* AI sekmeleri kaldırıldı — sadece GPT-4o */}

            {/* Tahmin özeti — GPT */}
            {(() => {
              const pred = reportPred.gpt_prediction || (reportPred.ai_provider === "gpt" ? reportPred.prediction : null);
              const hp   = reportPred.gpt_home_pct ?? reportPred.home_pct;
              const dp   = reportPred.gpt_draw_pct ?? reportPred.draw_pct;
              const ap   = reportPred.gpt_away_pct ?? reportPred.away_pct;
              const conf = reportPred.gpt_confidence ?? reportPred.confidence;
              const reasoning = reportPred.gpt_reasoning || (reportPred.ai_provider === "gpt" ? reportPred.reasoning : null);
              const cfg = C_AI.gpt;
              if (!pred) return (
                <div style={{ padding: "16px 20px", color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
                  GPT-4o analizi henüz yapılmamış.
                </div>
              );
              const n = normPred(pred);
              return (
                <div style={{ padding: "12px 20px", background: cfg.bg, borderBottom: "1px solid #f0f0f0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 18 }}>{n === "draw" ? "🤝" : n === "away" ? "✈️" : "🏠"}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: cfg.color }}>{pred}</span>
                    {conf != null && (
                      <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af",
                        background: "#fff", padding: "2px 10px", borderRadius: 20, border: "1px solid #e5e7eb" }}>
                        Güven: {conf}/100
                      </span>
                    )}
                  </div>
                  {hp != null && (
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", gap: 1, marginBottom: 4 }}>
                        <div style={{ flex: hp, background: "#2563eb" }} />
                        <div style={{ flex: dp, background: "#d97706" }} />
                        <div style={{ flex: ap, background: "#dc2626" }} />
                      </div>
                      <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#6b7280" }}>
                        <span><b style={{ color: "#2563eb" }}>🏠 {reportPred.home}</b> %{hp}</span>
                        <span><b style={{ color: "#d97706" }}>🤝</b> %{dp}</span>
                        <span><b style={{ color: "#dc2626" }}>✈️ {reportPred.away}</b> %{ap}</span>
                      </div>
                    </div>
                  )}
                  {/* Piyasa (odds) özeti */}
                  {reportPred.odds_bookmakers > 0 ? (
                    <div style={{ padding: "4px 10px", background: "#f0fdf4", borderRadius: 5, border: "1px solid #bbf7d0", fontSize: 11, marginBottom: 6 }}>
                      <b style={{ color: "#15803d" }}>📊 Piyasa</b>
                      <span style={{ color: "#166534", marginLeft: 6 }}>🏠 %{reportPred.odds_ev} · 🤝 %{reportPred.odds_ber} · ✈️ %{reportPred.odds_dep}</span>
                      <span style={{ color: "#6b7280", marginLeft: 6 }}>({reportPred.odds_bookmakers} bookie)</span>
                    </div>
                  ) : (
                    <div style={{ padding: "4px 10px", background: "#f9fafb", borderRadius: 5, border: "1px solid #e5e7eb", fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>📊 Piyasa verisi yok</div>
                  )}
                  {reasoning && (
                    <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6,
                      background: "#fff", borderRadius: 6, padding: "8px 12px", border: "1px solid #e5e7eb" }}>
                      💬 {reasoning}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Rapor içeriği */}
            <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
              {(() => {
                const reports = reportPred.gpt_reports || (reportPred.ai_provider === "gpt" ? reportPred.reports : null);
                if (!reports) return (
                  <div style={{ color: "#9ca3af", textAlign: "center", padding: 32 }}>Rapor mevcut değil.</div>
                );
                return (
                  <>
                    <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                      {[["form","📊 Form"],["stats","📈 İstatistik"],["news","📰 Haberler"],
                        ["home_case",`🏠 ${reportPred.home}`],["away_case",`✈️ ${reportPred.away}`],
                        ["odds","📊 Piyasa"],["kalibrasyon","🎯 Kalibrasyon"]].map(([k, v]) => (
                        <button key={k} onClick={() => setActiveReport(k)} style={{
                          padding: "4px 12px", borderRadius: 16, fontSize: 12,
                          border: `1px solid ${activeReport === k ? "#374151" : "#e5e7eb"}`,
                          background: activeReport === k ? "#111827" : "#fff",
                          color: activeReport === k ? "#fff" : "#6b7280",
                          cursor: "pointer", fontWeight: activeReport === k ? 600 : 400,
                        }}>{v}</button>
                      ))}
                    </div>
                    {activeReport === "kalibrasyon" ? (
                      <KalibrasyonView pred={reportPred} />
                    ) : activeReport === "odds" ? (
                      reportPred.odds_bookmakers > 0 ? (
                        <div>
                          <div style={{ marginBottom: 10, fontWeight: 600, color: "#15803d", fontSize: 13 }}>📊 Piyasa Oranları ({reportPred.odds_bookmakers} Bookmaker)</div>
                          {[[`🏠 ${reportPred.home} Kazanır`, reportPred.odds_ev, "#2563eb"], ["🤝 Beraberlik", reportPred.odds_ber, "#d97706"], [`✈️ ${reportPred.away} Kazanır`, reportPred.odds_dep, "#dc2626"]].map(([label, pct, color]) => (
                            <div key={label} style={{ marginBottom: 10 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color }}>{label}</span>
                                <span style={{ fontSize: 15, fontWeight: 800, color }}>%{pct}</span>
                              </div>
                              <div style={{ height: 7, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4 }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : <div style={{ textAlign: "center", padding: "24px 16px", color: "#9ca3af", fontSize: 12 }}>Piyasa verisi mevcut değil</div>
                    ) : (
                      <div style={{ fontSize: 13, lineHeight: 1.7, color: "#374151",
                        background: "#f9fafb", borderRadius: 8, padding: 14, whiteSpace: "pre-wrap" }}>
                        {reports[activeReport] || "Bu rapor mevcut değil."}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function League({league, active, onPredict, onBulkPredict, onViewPrediction, userAnalysisEnabled}) {
  const [tab,setTab]=useState("Haftalık Sonuçlar");
  const [predictions,setPredictions]=useState([]);
  const [allMatches,setAllMatches]=useState([]);
  const [matchesLoading,setMatchesLoading]=useState(true);
  const { queue, runningCount } = useAnalysis();
  const prevRunningRef = useRef(0);

  // Lig değişince sıfırla ve yeniden yükle — canlı maç varsa 30sn'de bir otomatik güncelle
  useEffect(()=>{
    const LIVE_S = ["1H","2H","HT","ET","BT","P","LIVE"];
    setTab("Haftalık Sonuçlar");
    setMatchesLoading(true);
    setAllMatches([]);
    let liveTimer = null;

    const fetchMatches = () => {
      api.matches(league,500).then(m=>{
        const matches = m.matches||[];
        setAllMatches(matches);
        setMatchesLoading(false);
        const hasLive = matches.some(x => LIVE_S.includes(x.status));
        if (hasLive && !liveTimer) {
          liveTimer = setInterval(fetchMatches, 30000);
        } else if (!hasLive && liveTimer) {
          clearInterval(liveTimer);
          liveTimer = null;
        }
      });
    };

    fetchMatches();
    fetchMergedPreds(league).then(setPredictions).catch(()=>{});
    return () => { if (liveTimer) { clearInterval(liveTimer); liveTimer = null; } };
  }, [league, active]);

  // Analiz bitince predictions'ı yenile
  useEffect(() => {
    if (prevRunningRef.current > 0 && runningCount === 0) {
      fetchMergedPreds(league).then(setPredictions).catch(()=>{});
    }
    prevRunningRef.current = runningCount;
  }, [runningCount, league]);



  return (
    <div className="main" style={active ? {} : {display:"none",pointerEvents:"none"}}>
      <div className="m-header">
        <div className="m-breadcrumb">Ligler / <b>{league} {(() => { const y = new Date().getMonth() >= 6 ? new Date().getFullYear() : new Date().getFullYear()-1; return y+"-"+(y+1).toString().slice(-2); })()}</b></div>
      </div>
      <div className="m-tabs-row">
        {TABS.map(t=><button key={t} className={tab===t?"m-tab active":"m-tab"} onClick={()=>setTab(t)}>{t}</button>)}
      </div>
      <div className="m-body">
        <div style={tab==="Haftalık Sonuçlar"?{}:{display:"none"}}>
          <WeeklyResults
            league={league}
            allMatches={allMatches}
            loading={matchesLoading}
            onPredict={onPredict}
            onBulkPredict={onBulkPredict}
            onViewPrediction={onViewPrediction}
            predictions={predictions}
            queue={queue}
            userAnalysisEnabled={userAnalysisEnabled}
          />
        </div>
        <div style={tab==="Puan Durumu"?{}:{display:"none"}}>
          <Standings league={league} allMatches={allMatches}/>
        </div>
        <div style={tab==="Takım İstatistikleri"?{}:{display:"none"}}>
          <TeamStats league={league} allMatches={allMatches}/>
        </div>
        <div style={tab==="Analizler"?{}:{display:"none"}}>
          <LeaguePredictions
            league={league}
            predictions={predictions}
            onPredict={onPredict}
            onBulkPredict={onBulkPredict}
          />
        </div>
      </div>
    </div>
  );
}
